(() => {
  if (customElements.get('yk-popular-clicks')) return;
  const FACETS = { color: 'Color', subject: 'Subject', style: 'Style', orientation: 'Orientation' };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const shortTitle = title => {
    const quoted = title.trim().match(/^["“](.*?)["”]/);
    return quoted ? quoted[1] : title.split(/ - | – | — | \| /)[0];
  };

  customElements.define('yk-popular-clicks', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.controller.signal });
      } else this.initialize();
    }

    async initialize() {
      try {
        this.ranks = JSON.parse(this.querySelector('[data-popular-ranks]').textContent);
        const response = await fetch(this.dataset.catalogUrl, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Catalog request failed');
        this.groups = (await response.json()).filter(group => group.products.length);
        if (!this.groups.length) throw new Error('No product groups received');
        this.catalog = Object.fromEntries(Object.keys(FACETS).map(key => [key, new Map()]));
        this.selected = Object.fromEntries(Object.keys(FACETS).map(key => [key, new Set()]));
        this.groups.forEach((group, index) => {
          group.index = index;
          group.products.forEach(product => {
            product.color.forEach(color => {
              if (color.name) this.catalog.color.set(color.name, color.code);
            });
            product.color = product.color.map(color => color.name).filter(Boolean);
            for (const key of Object.keys(FACETS)) {
              product[key] = product[key].filter(Boolean);
              for (const value of product[key]) if (!this.catalog[key].has(value)) this.catalog[key].set(value, null);
            }
          });
          group.products.sort((a, b) => (this.ranks[a.handle] ?? Number.MAX_SAFE_INTEGER) - (this.ranks[b.handle] ?? Number.MAX_SAFE_INTEGER));
        });
        this.page = 1;
        this.pageSize = Number(this.dataset.pageSize) || 6;
        this.productRequests = new Map();
        this.productQueue = [];
        this.activeRequests = 0;
        this.cardObserver = new IntersectionObserver(entries => {
          entries.filter(entry => entry.isIntersecting).forEach(entry => {
            this.cardObserver.unobserve(entry.target);
            this.loadCardImage(entry.target);
            this.productQueue.push(entry.target);
          });
          this.drainProductQueue();
        }, { rootMargin: '150px' });
        this.buildFacets();
        await this.setupFilterDrawer();
        this.bindEvents();
        this.observer = new ResizeObserver(() => this.updateArrows());
        this.observer.observe(this.querySelector('[data-groups]'));
        this.render();
        this.dataset.ready = 'true';
      } catch (error) {
        this.querySelector('[data-error]').hidden = false;
        this.querySelector('.yk-popular__results').setAttribute('aria-busy', 'false');
        console.error('Popular Clicks initialization failed', error);
      }
    }

    async setupFilterDrawer() {
      await customElements.whenDefined('x-drawer');
      if (!customElements.get('yk-popular-filter-drawer')) {
        customElements.define('yk-popular-filter-drawer', class extends customElements.get('x-drawer') {
          get shouldAppendToBody() { return false; }
        });
      }
      const panel = this.querySelector('.yk-popular__filters');
      const body = this.querySelector('.yk-popular__body');
      const drawer = element('yk-popular-filter-drawer', 'yk-popular-filter-drawer facets-drawer drawer drawer--sm color-scheme color-scheme--dialog');
      drawer.id = `PopularFilterDrawer-${this.closest('[id]').id}`;
      const title = element('p', 'h4', 'Filters'); title.slot = 'header';
      const footer = element('div'); footer.slot = 'footer';
      const apply = element('button', 'button w-full', 'Apply filters');
      apply.type = 'button'; apply.dataset.applyFilters = ''; footer.append(apply);
      drawer.append(title, footer); this.append(drawer); this.filterDrawer = drawer;
      this.querySelector('[data-filter-toggle]').setAttribute('aria-controls', drawer.id);
      this.filterMedia = matchMedia('(max-width: 799px)');
      const arrange = async () => {
        if (this.filterMedia.matches) drawer.insertBefore(panel, footer);
        else {
          if (drawer.open) await drawer.hide();
          body.prepend(panel);
        }
      };
      this.filterMedia.addEventListener('change', arrange, { signal: this.controller.signal });
      arrange();
    }

    buildFacets() {
      this.inputs = [];
      const target = this.querySelector('[data-facets]');
      for (const [key, title] of Object.entries(FACETS)) {
        const details = element('details', 'yk-popular__facet');
        details.open = true;
        details.append(element('summary', '', title));
        const options = element('div', `yk-popular__options${key === 'color' ? ' yk-popular__colors' : ''}`);
        for (const [value, color] of [...this.catalog[key]].sort((a, b) => a[0].localeCompare(b[0], 'en'))) {
          const label = element('label', key === 'color' ? 'yk-popular__color' : 'yk-popular__option');
          const input = element('input');
          input.type = 'checkbox'; input.value = value; input.dataset.facet = key;
          input.setAttribute('aria-label', value);
          label.append(input);
          const text = element('span', key === 'color' ? 'yk-popular__swatch' : '', key === 'color' ? undefined : value);
          if (key === 'color') {
            text.style.backgroundColor = color || value;
            label.title = value;
          }
          label.append(text); options.append(label);
          this.inputs.push({ input, label, text, key, value });
        }
        details.append(options); target.append(details);
      }
    }

    bindEvents() {
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        const key = event.target.dataset.facet;
        if (key) {
          if (event.target.checked) this.selected[key].add(event.target.value);
          else this.selected[key].delete(event.target.value);
          this.page = 1; this.render();
        } else if (event.target.matches('[data-sort]')) {
          this.page = 1; this.render();
        }
      }, options);
      window.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button) || button.hasAttribute('data-group-dropdown') || button.closest('.yk-group-dropdown-popover')) return;
        event.stopPropagation();
        if (button.hasAttribute('data-clear')) {
          Object.values(this.selected).forEach(set => set.clear());
          this.page = 1; this.render();
        } else if (button.dataset.removeFacet) {
          this.selected[button.dataset.removeFacet].delete(button.dataset.removeValue);
          this.page = 1; this.render();
        } else if (button.dataset.layoutChoice) {
          this.dataset.layout = button.dataset.layoutChoice;
          this.querySelectorAll('[data-layout-choice]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
          this.querySelectorAll('.yk-popular__track').forEach(track => { track.scrollLeft = 0; });
          requestAnimationFrame(() => this.updateArrows());
        } else if (button.hasAttribute('data-filter-toggle')) {
          this.filterDrawer.show();
        } else if (button.hasAttribute('data-apply-filters')) {
          this.filterDrawer.hide();
        } else if (button.dataset.page || button.dataset.pageNumber) {
          this.page = button.dataset.pageNumber ? Number(button.dataset.pageNumber) : this.page + (button.dataset.page === 'next' ? 1 : -1);
          this.render();
          this.querySelector('.yk-popular__toolbar').scrollIntoView({ block: 'start' });
        } else if (button.dataset.direction) {
          const track = button.closest('[data-group]').querySelector('.yk-popular__track');
          track.scrollBy({ left: Number(button.dataset.direction) * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }, { ...options, capture: true });
      this.addEventListener('keydown', event => {
        if (!event.target.matches('.yk-popular__track') || !['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const track = event.target;
        track.scrollLeft = event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth : track.scrollLeft + (event.key === 'ArrowLeft' ? -1 : 1) * track.clientWidth;
      }, options);
    }

    matchesProduct(product, ignoreFacet) {
      return Object.entries(this.selected).every(([key, values]) => key === ignoreFacet || !values.size || product[key].some(value => values.has(value)));
    }

    sortedGroups() {
      const groups = this.groups.filter(group => group.products.some(product => this.matchesProduct(product)));
      const mode = this.querySelector('[data-sort]').value;
      const direction = mode.endsWith('descending') ? -1 : 1;
      const hasFilters = Object.values(this.selected).some(values => values.size);
      const score = group => hasFilters ? group.products.filter(product => this.matchesProduct(product)).length / group.products.length : 0;
      groups.sort((a, b) => {
        const x = a.products[0], y = b.products[0];
        let difference = 0;
        if (mode === 'best-selling') difference = (this.ranks[x.handle] ?? Number.MAX_SAFE_INTEGER) - (this.ranks[y.handle] ?? Number.MAX_SAFE_INTEGER);
        else if (mode === 'most-relevant') difference = score(b) - score(a);
        else if (mode.startsWith('title-')) difference = shortTitle(x.title).localeCompare(shortTitle(y.title), 'en', { sensitivity: 'base', numeric: true });
        else if (mode.startsWith('price-')) difference = x.price - y.price;
        else if (mode.startsWith('created-')) difference = Number(x.created) - Number(y.created);
        return difference * direction || a.index - b.index;
      });
      return groups;
    }

    render() {
      const groups = this.sortedGroups();
      const pages = Math.max(1, Math.ceil(groups.length / this.pageSize));
      this.page = Math.min(Math.max(1, this.page), pages);
      const count = new Set(groups.flatMap(group => group.products.map(product => product.handle))).size;
      this.querySelector('[data-count]').textContent = `${count.toLocaleString('en')} products · ${groups.length} product groups`;
      this.querySelector('[data-empty]').hidden = groups.length > 0;
      this.cardObserver.disconnect();
      this.productQueue = [];
      this.querySelector('[data-groups]').replaceChildren(...groups.slice((this.page - 1) * this.pageSize, this.page * this.pageSize).map(group => this.groupNode(group)));
      this.querySelectorAll('[data-product]').forEach(card => this.cardObserver.observe(card));
      this.querySelector('[data-pagination]').hidden = pages <= 1;
      this.querySelector('[data-page-label]').textContent = `${this.page} / ${pages}`;
      const numbers = [...new Set([1, this.page - 1, this.page, this.page + 1, pages])].filter(n => n > 0 && n <= pages).sort((a, b) => a - b);
      const numberNodes = [];
      numbers.forEach((n, index) => {
        if (index && n > numbers[index - 1] + 1) numberNodes.push(element('span', '', '…'));
        const button = element('button', '', String(n));
        button.type = 'button'; button.dataset.pageNumber = n;
        button.setAttribute('aria-label', `Page ${n}`);
        if (n === this.page) button.setAttribute('aria-current', 'page');
        numberNodes.push(button);
      });
      this.querySelector('[data-page-numbers]').replaceChildren(...numberNodes);
      this.querySelector('[data-page="previous"]').disabled = this.page === 1;
      this.querySelector('[data-page="next"]').disabled = this.page === pages;
      this.updateFacets();
      this.querySelector('.yk-popular__results').setAttribute('aria-busy', 'false');
      requestAnimationFrame(() => this.updateArrows());
    }

    updateFacets() {
      const counts = Object.fromEntries(Object.keys(FACETS).map(key => [key, new Map()]));
      for (const key of Object.keys(FACETS)) {
        for (const group of this.groups) {
          const values = new Set(group.products.filter(product => this.matchesProduct(product, key)).flatMap(product => product[key]));
          for (const value of values) counts[key].set(value, (counts[key].get(value) || 0) + 1);
        }
      }
      for (const { input, label, text, key, value } of this.inputs) {
        const count = counts[key].get(value) || 0;
        input.checked = this.selected[key].has(value);
        input.disabled = !count && !input.checked;
        input.setAttribute('aria-label', `${value}, ${count} matching groups`);
        label.title = `${value}: ${count} matching groups`;
        if (key !== 'color') text.textContent = `${value} (${count})`;
      }
      const chips = [];
      for (const [key, values] of Object.entries(this.selected)) for (const value of values) {
        const button = element('button', '', `${value} ×`);
        button.type = 'button'; button.dataset.removeFacet = key; button.dataset.removeValue = value;
        button.setAttribute('aria-label', `Remove ${value} filter`); chips.push(button);
      }
      this.querySelector('[data-active]').replaceChildren(...chips);
      this.querySelector('[data-clear]').hidden = !chips.length;
    }

    groupNode(group) {
      const section = element('section', 'yk-popular__group');
      section.dataset.group = group.key;
      const groupTitle = shortTitle(group.products[0].title);
      const heading = element('h2', 'yk-popular__group-title h4', groupTitle);
      heading.id = `PopularTitle-${group.key}`;
      section.setAttribute('aria-labelledby', heading.id);
      const header = element('div', 'yk-popular__group-header');
      const controls = element('div', 'yk-popular__group-controls');
      for (const direction of [-1, 1]) {
        const button = element('button', '', direction === -1 ? '←' : '→');
        button.type = 'button'; button.dataset.direction = direction;
        button.setAttribute('aria-label', `${direction === -1 ? 'Previous' : 'Next'} artworks in group ${group.index + 1}`);
        controls.append(button);
      }
      const track = element('div', 'yk-popular__track');
      track.id = `PopularTrack-${group.key}`; track.tabIndex = 0;
      track.setAttribute('role', 'region'); track.setAttribute('aria-label', `Artwork group ${group.index + 1} products`);
      track.append(...group.products.map(product => this.cardNode(product)));
      track.addEventListener('scroll', () => this.updateTrackArrows(section), { passive: true });
      header.append(heading, controls);
      section.append(header, track); return section;
    }

    cardNode(product) {
      const card = element('article', 'yk-popular__card'); card.dataset.product = product.handle;
      const media = element('a', 'yk-popular__media'); media.href = product.url;
      if (product.image) {
        const image = element('img'); image.dataset.imageSrc = product.image; image.alt = product.title;
        image.width = 800; image.height = 800; image.loading = 'eager'; image.decoding = 'async';
        media.append(image);
      } else media.append(element('span', '', 'Artwork image unavailable'));
      const sale = product.compare > product.price;
      if (sale || !product.available) media.append(element('span', 'yk-popular__badge', product.available ? 'On sale' : 'Sold out'));
      const info = element('div', 'yk-popular__info');
      const title = element('a', 'yk-popular__title', product.title); title.href = product.url;
      const prices = element('div', 'yk-popular__prices');
      prices.append(element('span', sale ? 'yk-popular__sale' : '', `${product.varies ? 'From ' : ''}${product.money}`));
      if (sale) prices.append(element('s', 'yk-popular__compare', product.compareMoney));
      info.append(title, prices); card.append(media, info); card.productData = product; return card;
    }

    loadCardImage(card) {
      const image = card.querySelector('img[data-image-src]');
      if (!image) return;
      const url = new URL(image.dataset.imageSrc, location.origin);
      const width = Math.ceil(card.querySelector('.yk-popular__media').getBoundingClientRect().width) || 400;
      image.sizes = `${width}px`;
      image.srcset = [240, 400, 600, 800, 1200].map(size => {
        url.searchParams.set('width', size);
        return `${url.href} ${size}w`;
      }).join(', ');
      url.searchParams.set('width', 800);
      image.src = url.href;
      delete image.dataset.imageSrc;
    }

    drainProductQueue() {
      while (this.activeRequests < 4 && this.productQueue.length) {
        const card = this.productQueue.shift();
        if (!card.isConnected) continue;
        this.activeRequests++;
        this.refreshCard(card).finally(() => {
          this.activeRequests--;
          this.drainProductQueue();
        });
      }
    }

    async refreshCard(card) {
      const handle = card.dataset.product;
      try {
        if (!this.productRequests.has(handle)) {
          const root = this.dataset.rootUrl.replace(/\/$/, '');
          this.productRequests.set(handle, fetch(`${root}/products/${encodeURIComponent(handle)}.js`, { signal: this.controller.signal }).then(response => {
            if (!response.ok) throw new Error('Product refresh failed');
            return response.json();
          }));
        }
        const latest = await this.productRequests.get(handle);
        if (!card.isConnected) return;
        const currency = window.Shopify?.currency?.active || 'USD';
        const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value / 100) + ` ${currency}`;
        const selectedImage = latest.media?.[1]?.preview_image?.src || latest.media?.[0]?.preview_image?.src || latest.featured_image;
        const updated = { ...card.productData, title: latest.title, url: latest.url, price: latest.price_min,
          money: money(latest.price_min), compare: latest.compare_at_price || 0,
          compareMoney: money(latest.compare_at_price || 0), available: latest.available, varies: latest.price_varies,
          image: selectedImage ? selectedImage + (selectedImage.includes('?') ? '&' : '?') + 'width=800' : null };
        const replacement = this.cardNode(updated);
        card.querySelector('.yk-popular__prices').replaceWith(replacement.querySelector('.yk-popular__prices'));
        const title = card.querySelector('.yk-popular__title');
        title.textContent = updated.title; title.href = updated.url;
        const media = card.querySelector('.yk-popular__media'); media.href = updated.url;
        media.querySelector('.yk-popular__badge')?.remove();
        const badge = replacement.querySelector('.yk-popular__badge');
        if (badge) media.append(badge);
        if (updated.image !== card.productData.image) {
          const image = replacement.querySelector('img');
          const previous = media.querySelector('img');
          if (image) {
            if (previous) previous.replaceWith(image);
            else media.prepend(image);
            this.loadCardImage(card);
          }
        } else if (media.querySelector('img')) media.querySelector('img').alt = updated.title;
        card.productData = updated;
        card.dataset.live = 'true';
      } catch (error) {
        if (error.name !== 'AbortError') this.productRequests.delete(handle);
      }
    }

    updateTrackArrows(group) {
      const track = group.querySelector('.yk-popular__track');
      group.querySelector('[data-direction="-1"]').disabled = track.scrollLeft <= 2;
      group.querySelector('[data-direction="1"]').disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }

    updateArrows() {
      this.querySelectorAll('[data-group]').forEach(group => this.updateTrackArrows(group));
      this.querySelectorAll('.yk-popular__media img[src]').forEach(image => {
        const size = `${Math.ceil(image.parentElement.getBoundingClientRect().width)}px`;
        if (image.sizes !== size) image.sizes = size;
      });
    }

    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.cardObserver?.disconnect();
    }
  });
})();
